import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function RatingDialog({ open, onOpenChange, target, onDone }) {
  // target: { ratee_id, ratee_name, ref_id, context, role }
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!comment.trim()) { toast.error("Add a short comment"); return; }
    setSaving(true);
    try {
      await api.post("/user-reviews", {
        ratee_id: target.ratee_id, rating, comment,
        ref_id: target.ref_id, context: target.context,
      });
      toast.success("Rating submitted");
      setComment(""); setRating(5);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit rating");
    } finally { setSaving(false); }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Rate {target.ratee_name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">{target.role}</p>
        <div className="flex items-center gap-1.5 mt-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`rate-star-${n}`} onClick={() => setRating(n)}>
              <Star className={`w-8 h-8 transition-colors ${n <= rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-black/20"}`} />
            </button>
          ))}
        </div>
        <Textarea
          data-testid="rating-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="How was your experience with this person?"
          className="mt-2 bg-white border-black/10 rounded-xl"
        />
        <button
          data-testid="submit-rating-btn"
          onClick={submit}
          disabled={saving}
          className="mt-2 w-full bg-[#0A0A0A] text-white rounded-full py-3 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60"
        >
          {saving ? "Submitting…" : "Submit rating"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
